import { Link } from 'react-router-dom';
import { TrendingUp, Lock, LogIn, CheckCircle2 } from 'lucide-react';
import PageLayout, { PageCTA } from '../components/PageLayout';
import TradingViewTicker from '../components/TradingViewTicker';
import TradingViewOverview from '../components/TradingViewOverview';
import { IMAGES } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

const COMMODITIES = ['cuivre', 'aluminium', 'soufre', 'potasse', 'bitume', 'or'];

export default function TradingPage() {
  const { t } = useLang();
  const { isAuth, user } = useAuth();
  const p = t('pages.trading');

  return (
    <PageLayout crumb={p.breadcrumb} title={p.hero} subtitle={p.sub} heroImage="https://images.pexels.com/photos/28185415/pexels-photo-28185415.jpeg">
      {/* Live prices */}
      <section className="py-14 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-8">
            <span className="text-brand-yellow text-xs font-bold tracking-widest uppercase">Cotations en direct</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">Prix des matières premières en temps réel</h2>
          </div>
          <div className="rounded-lg overflow-hidden border border-white/10 mb-4">
            <TradingViewTicker />
          </div>
          <div className="rounded-xl overflow-hidden border border-white/10 bg-brand-navy">
            <TradingViewOverview />
          </div>
          <p className="text-white/50 text-xs text-center mt-4">Données fournies par TradingView · COMEX, NYMEX, CBOT · Mises à jour en temps réel</p>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-label mb-4">01</span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mt-4 mb-4">{p.section1Title}</h2>
            <p className="text-gray-700 leading-relaxed">{p.section1Body}</p>
          </div>
          <img src={IMAGES.containers} alt="" className="rounded-xl w-full h-96 object-cover shadow-lg" />
        </div>
      </section>

      {/* Commodities handled */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-navy text-center mb-12">{p.section2Title}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {COMMODITIES.map((id) => (
              <div key={id} className="p-6 bg-white rounded-lg border border-gray-100 text-center hover-lift hover:border-brand-yellow">
                <TrendingUp className="w-6 h-6 text-brand-yellow mx-auto mb-2" />
                <div className="text-brand-navy font-bold text-lg">{t(`trading.names.${id}`)}</div>
                <div className="text-xs text-gray-500 mt-1">{t(`trading.commodityDescs.${id}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Private access */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="bg-brand-navy rounded-2xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-yellow/15 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow mb-4">
                  <Lock className="w-6 h-6 text-brand-yellow" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight">{t('ext.pa_titleLong')}</h3>
                <p className="text-white/80 mt-4 leading-relaxed">
                  {t('ext.pa_descLong')}
                </p>
              </div>
              <div>
                <ul className="space-y-3 mb-6 text-white">
                  {[
                    t('ext.pa_bullet_loi'),
                    t('ext.pa_bullet_icpo'),
                    t('ext.pa_bullet_bank'),
                    t('ext.pa_bullet_pass'),
                    t('ext.pa_bullet_upload'),
                  ].map((it, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-brand-yellow shrink-0" />
                      <span className="text-sm">{it}</span>
                    </li>
                  ))}
                </ul>
                {isAuth ? (
                  <Link to={user?.role === 'admin' ? '/admin' : '/extranet'} className="btn-yellow w-full justify-center">
                    <LogIn className="w-4 h-4" /> {t('ext.pa_ctaEnter')}
                  </Link>
                ) : (
                  <Link to="/extranet/login" className="btn-yellow w-full justify-center">
                    <LogIn className="w-4 h-4" /> {t('ext.pa_ctaSignIn')}
                  </Link>
                )}
                <p className="text-xs text-white/50 text-center mt-3">
                  {t('ext.pa_contactPlain')} <a href="mailto:denis@yuville.com" className="text-brand-yellow hover:underline">denis@yuville.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PageCTA title={p.ctaTitle} body={p.ctaBody} />
    </PageLayout>
  );
}
